/**
 * Audit Management API Service
 * Handles all audit schedule, engagement, finding, workpaper, and report operations
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  config.headers = { ...config.headers, ...getAuthHeaders() };
  return config;
});

// ==================== Audit Schedule ====================

export const getAuditSchedules = async (params = {}) => {
  const response = await api.get('/api/audit/schedule', { params });
  return response.data;
};

export const getAuditScheduleById = async (scheduleId) => {
  const response = await api.get(`/api/audit/schedule/${scheduleId}`);
  return response.data;
};

export const createAuditSchedule = async (scheduleData) => {
  const response = await api.post('/api/audit/schedule', scheduleData);
  return response.data;
};

export const updateAuditSchedule = async (scheduleId, updateData) => {
  const response = await api.put(`/api/audit/schedule/${scheduleId}`, updateData);
  return response.data;
};

// ==================== Audit Engagements ====================

export const getAuditEngagements = async (params = {}) => {
  const response = await api.get('/api/audit/engagements', { params });
  return response.data;
};

export const getAuditEngagementById = async (engagementId) => {
  const response = await api.get(`/api/audit/engagements/${engagementId}`);
  return response.data;
};

export const createAuditEngagement = async (engagementData) => {
  const response = await api.post('/api/audit/engagements', engagementData);
  return response.data;
};

export const updateAuditEngagement = async (engagementId, updateData) => {
  const response = await api.put(`/api/audit/engagements/${engagementId}`, updateData);
  return response.data;
};

export const deleteAuditEngagement = async (engagementId) => {
  const response = await api.delete(`/api/audit/engagements/${engagementId}`);
  return response.data;
};

// ==================== Audit Findings ====================

export const getAuditFindings = async (params = {}) => {
  const response = await api.get('/api/audit/findings', { params });
  return response.data;
};

export const getAuditFindingById = async (findingId) => {
  const response = await api.get(`/api/audit/findings/${findingId}`);
  return response.data;
};

export const createAuditFinding = async (findingData) => {
  const response = await api.post('/api/audit/findings', findingData);
  return response.data;
};

export const updateAuditFinding = async (findingId, updateData) => {
  const response = await api.put(`/api/audit/findings/${findingId}`, updateData);
  return response.data;
};

export const updateFindingRemediation = async (findingId, remediationData) => {
  const response = await api.put(`/api/audit/findings/${findingId}/remediate`, remediationData);
  return response.data;
};

export const verifyFindingRemediation = async (findingId, verificationData) => {
  const response = await api.put(`/api/audit/findings/${findingId}/verify`, verificationData);
  return response.data;
};

// ==================== Audit Workpapers ====================

export const getAuditWorkpapers = async (params = {}) => {
  const response = await api.get('/api/audit/workpapers', { params });
  return response.data;
};

export const getAuditWorkpaperById = async (workpaperId) => {
  const response = await api.get(`/api/audit/workpapers/${workpaperId}`);
  return response.data;
};

export const createAuditWorkpaper = async (workpaperData) => {
  const response = await api.post('/api/audit/workpapers', workpaperData);
  return response.data;
};

export const updateAuditWorkpaper = async (workpaperId, updateData) => {
  const response = await api.put(`/api/audit/workpapers/${workpaperId}`, updateData);
  return response.data;
};

export const reviewAuditWorkpaper = async (workpaperId, reviewData) => {
  const response = await api.put(`/api/audit/workpapers/${workpaperId}/review`, reviewData);
  return response.data;
};

// ==================== Audit Reports ====================

export const getAuditReports = async (params = {}) => {
  const response = await api.get('/api/audit/reports', { params });
  return response.data;
};

export const createAuditReport = async (reportData) => {
  const response = await api.post('/api/audit/reports', reportData);
  return response.data;
};

export const updateAuditReport = async (reportId, updateData) => {
  const response = await api.put(`/api/audit/reports/${reportId}`, updateData);
  return response.data;
};

// ==================== Dashboard & Calendar ====================

export const getAuditDashboard = async () => {
  const response = await api.get('/api/audit/dashboard');
  return response.data;
};

export const getAuditCalendar = async (params = {}) => {
  const response = await api.get('/api/audit/calendar', { params });
  return response.data;
};

// ==================== Helper Functions ====================

export const AUDIT_TYPES = [
  { value: 'internal', label: 'Internal Audit', icon: '🏢' },
  { value: 'external', label: 'External Audit', icon: '🌐' },
  { value: 'soc2', label: 'SOC 2', icon: '🔐' },
  { value: 'iso27001', label: 'ISO 27001', icon: '📋' },
  { value: 'pci_dss', label: 'PCI DSS', icon: '💳' },
  { value: 'hipaa', label: 'HIPAA', icon: '🏥' },
  { value: 'gdpr', label: 'GDPR', icon: '🇪🇺' },
  { value: 'regulatory', label: 'Regulatory', icon: '⚖️' }
];

export const AUDIT_STATUSES = [
  { value: 'planned', label: 'Planned', color: 'gray' },
  { value: 'in_progress', label: 'In Progress', color: 'blue' },
  { value: 'review', label: 'Under Review', color: 'yellow' },
  { value: 'completed', label: 'Completed', color: 'green' },
  { value: 'cancelled', label: 'Cancelled', color: 'red' }
];

export const FINDING_SEVERITIES = [
  { value: 'critical', label: 'Critical', color: 'red', bgColor: 'bg-red-100' },
  { value: 'high', label: 'High', color: 'orange', bgColor: 'bg-orange-100' },
  { value: 'medium', label: 'Medium', color: 'yellow', bgColor: 'bg-yellow-100' },
  { value: 'low', label: 'Low', color: 'blue', bgColor: 'bg-blue-100' },
  { value: 'informational', label: 'Informational', color: 'gray', bgColor: 'bg-gray-100' }
];

export const FINDING_STATUSES = [
  { value: 'open', label: 'Open', color: 'red' },
  { value: 'in_progress', label: 'In Progress', color: 'yellow' },
  { value: 'remediated', label: 'Remediated', color: 'blue' },
  { value: 'verified', label: 'Verified', color: 'green' },
  { value: 'closed', label: 'Closed', color: 'gray' },
  { value: 'risk_accepted', label: 'Risk Accepted', color: 'purple' }
];

export const WORKPAPER_TYPES = [
  { value: 'testing', label: 'Control Testing' },
  { value: 'interview', label: 'Interview Notes' },
  { value: 'walkthrough', label: 'Walkthrough' },
  { value: 'document_review', label: 'Document Review' }
];

export const getSeverityColor = (severity) => {
  const sev = FINDING_SEVERITIES.find(s => s.value === severity);
  return sev?.color || 'gray';
};

export const getStatusColor = (status) => {
  const stat = AUDIT_STATUSES.find(s => s.value === status) || FINDING_STATUSES.find(s => s.value === status);
  return stat?.color || 'gray';
};

export const getAuditTypeLabel = (type) => {
  const t = AUDIT_TYPES.find(a => a.value === type);
  return t?.label || type;
};

export const getAuditTypeIcon = (type) => {
  const t = AUDIT_TYPES.find(a => a.value === type);
  return t?.icon || '📋';
};

export default {
  getAuditSchedules,
  getAuditScheduleById,
  createAuditSchedule,
  updateAuditSchedule,
  getAuditEngagements,
  getAuditEngagementById,
  createAuditEngagement,
  updateAuditEngagement,
  deleteAuditEngagement,
  getAuditFindings,
  getAuditFindingById,
  createAuditFinding,
  updateAuditFinding,
  updateFindingRemediation,
  verifyFindingRemediation,
  getAuditWorkpapers,
  getAuditWorkpaperById,
  createAuditWorkpaper,
  updateAuditWorkpaper,
  reviewAuditWorkpaper,
  getAuditReports,
  createAuditReport,
  updateAuditReport,
  getAuditDashboard,
  getAuditCalendar
};
